import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListSubheader,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { useColorScheme } from "@mui/material/styles";
import DarkModeIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeIcon from "@mui/icons-material/LightModeOutlined";

import {
  KernelMark,
  Loading,
  Permission,
  Tooltip,
  kernel,
  kernelDelete,
  kernelPost,
  resume,
  signIn,
  signOut,
} from "@lopie/kernel-ui";

import { parseHash } from "./hash.js";
import { GROUPS, SECTIONS } from "./sections/index.jsx";

const NAV_WIDTH = 232;

/**
 * The nav: four jobs, twelve questions under them.
 *
 * ## The questions are the design
 *
 * A label with a question under it was already the most distinctive thing in
 * this interface — *"Is the kernel telling the truth?"* says more about what
 * this product is than any icon could, and an icon set for "manifest" and
 * "service_reads" does not exist and should not be invented.
 *
 * It was set as 0.75rem secondary text, which is where you put a thing you
 * are slightly embarrassed by. So: the label is the label, the question is
 * given room, and the selected item is marked with a rule down its edge
 * rather than a filled block — the same two pixels of brand the `Headline`
 * uses, meaning the same thing, which is *you are here*.
 *
 * ## Why they are grouped now
 *
 * Twelve of them in one list was twelve questions the kernel can answer, in
 * the order the kernel was built. Read top to bottom it asked somebody to
 * hold the whole system in their head to find the one screen they wanted,
 * and the two screens that belong beside each other — spend and the apps
 * spending it, the log and the permissions reconciled against it — were four
 * rows apart.
 *
 * Nothing is hidden behind a group. All twelve are on screen at once, which
 * is the point: a group is a heading, not a drawer, and an administrator who
 * knows where they are going should never have to open something first.
 *
 * The question hides on anything but the selected row at narrow widths,
 * because twelve questions stacked is an essay and one question is a label.
 */
function Nav({ current, onSelect }) {
  return (
    <Box sx={{ width: NAV_WIDTH, flexShrink: 0 }}>
      <List dense disablePadding sx={{ px: 1.25, py: 2 }}>
        {GROUPS.map((group) => {
          // Filter rather than sort, so a section's place inside its group is
          // its place in `SECTIONS` and there is one ordering to edit.
          const mine = SECTIONS.filter((s) => s.group === group.id);
          if (mine.length === 0) return null;
          return (
            <Box component="li" key={group.id} sx={{ listStyle: "none" }}>
              <ListSubheader
                // `div`, because the wrapper below is already the <li> and
                // MUI renders this as one by default — which is an <li>
                // inside an <li>, invalid, and React says so in the dev
                // console.
                component="div"
                disableGutters
                disableSticky
                title={group.blurb}
                sx={{
                  bgcolor: "transparent",
                  color: "text.secondary",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  lineHeight: 1,
                  px: 1.5,
                  pt: 2.5,
                  pb: 1,
                }}
              >
                {group.label}
              </ListSubheader>
              <List dense disablePadding>
                {mine.map((section) => {
                  const here = current === section.id;
                  return (
                    <ListItemButton
                      key={section.id}
                      selected={here}
                      onClick={() => onSelect(section.id)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.25,
                        pl: 1.5,
                        borderLeft: 2,
                        borderColor: here ? "primary.main" : "transparent",
                        transition:
                          "border-color 140ms, background-color 140ms",
                        "@media (prefers-reduced-motion: reduce)": {
                          transition: "none",
                        },
                        "&.Mui-selected": { backgroundColor: "action.hover" },
                      }}
                    >
                      <ListItemText
                        primary={section.label}
                        secondary={section.question}
                        // `sx`, not bare props. These are `Typography` slots
                        // and v9 stopped forwarding system props on
                        // `Typography`, so `fontSize` was dropped and
                        // `lineHeight` reached the DOM as `lineheight="1.4"`
                        // on all twelve rows. The questions have been
                        // rendering at 14px, not the 0.75rem asked for here,
                        // for as long as this file has existed — and the
                        // comment above claiming they were set small was
                        // describing an edit that never applied.
                        slotProps={{
                          primary: {
                            sx: {
                              fontWeight: here ? 600 : 500,
                              color: here ? "primary.main" : "text.primary",
                            },
                          },
                          secondary: {
                            sx: { fontSize: "0.75rem", lineHeight: 1.4 },
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </List>
    </Box>
  );
}

function SchemeToggle() {
  const { mode, setMode } = useColorScheme();
  // Three states, not two. `system` is the default and most viewers never
  // leave it, so the toggle cycles rather than flipping a boolean.
  const next = mode === "dark" ? "light" : mode === "light" ? "system" : "dark";
  const label =
    mode === "system" ? "Following your system" : `${mode} — click for ${next}`;

  return (
    <Tooltip title={label}>
      <IconButton size="small" onClick={() => setMode(next)} aria-label={label}>
        {mode === "dark" ? (
          <DarkModeIcon fontSize="small" />
        ) : (
          <LightModeIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
}

/** The wordmark, and the attribution beside it.
 *
 * The word "Kernel" used to be set in the body font here, which made the
 * console the one Kernel surface that did not carry the mark — the sign-in
 * page it hands you to does. `KernelMark` is the same shape Cognito serves,
 * from the same source file, in `currentColor` so it is correct in both
 * schemes.
 *
 * Attribution, not headline: Kernel is what a customer governs their data
 * through; Lopie One is who makes it. */
function Brand() {
  return (
    <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
      <KernelMark height={22} />
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", display: { xs: "none", sm: "block" } }}
      >
        powered by Lopie One
      </Typography>
    </Stack>
  );
}

/**
 * Only ever seen when something went wrong, or just after signing out.
 *
 * The happy path does not stop here. Asking somebody to click "sign in" on a
 * page with nothing else on it is asking them to confirm the thing they
 * already did by opening the console, and the click was the first thing
 * anybody complained about.
 */
function SignedOut({ error, signedOut }) {
  return (
    <Box sx={{ display: "grid", placeItems: "center", minHeight: "100vh", px: 3 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 420, textAlign: "center" }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Brand />
        </Box>
        {error ? (
          <Alert severity="error" sx={{ textAlign: "left" }}>
            {error}
          </Alert>
        ) : (
          <Typography color="text.secondary">
            {signedOut
              ? "Signed out. Everything here is read from Kernel using your sign-in, so there is nothing left on this page."
              : "Taking you to sign in…"}
          </Typography>
        )}
        <Box>
          <Button variant="contained" onClick={signIn}>
            {error ? "Try again" : "Sign in"}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(() => {
    // Deep-link to a section without needing a router. A router earns its
    // place when there are URLs worth sharing; ten tabs is not that yet.
    const asked = parseHash(window.location.hash).id;
    // `library` was its own screen until it merged into Apps. A stale link
    // would otherwise land on the Portal, which is a different screen giving
    // a different answer — worse than a slow redirect and harder to notice.
    return (asked === "library" ? "apps" : asked) || SECTIONS[0].id;
  });

  // What the section on screen last resolved as its effective permission.
  // Lifted here because the "why am I seeing this" strip belongs to the shell:
  // making each page remember to render it is how three of six forget.
  const [effective, setEffective] = useState(null);

  useEffect(() => {
    resume()
      .then((found) => {
        setSession(found);
        // No session and nothing went wrong: go straight to the sign-in page
        // rather than parking on an interstitial whose only content is a
        // button that does this.
        //
        // `signedout=1` is how a deliberate sign-out avoids being bounced
        // back in, which would make the sign-out button do nothing visible.
        const deliberate = new URLSearchParams(window.location.search).has(
          "signedout",
        );
        if (!found && !deliberate) {
          signIn();
          return;
        }
        setReady(true);
      })
      .catch((e) => {
        setError(e.message);
        setReady(true);
      });
  }, []);

  useEffect(() => {
    // Not when the address already names this section: a link may carry
    // parameters after a `?` (`#people?user=...&tag=...`) that the section
    // reads once it is open, and rewriting the hash here would drop them.
    if (parseHash(window.location.hash).id !== current) {
      window.location.hash = current;
    }
    setEffective(null);
  }, [current]);

  // The other half of the deep link, and it was missing. The hash was written
  // on every section change and read exactly once, at mount — so the back
  // button did nothing, and a link somebody pasted into Slack opened whichever
  // screen their console had been left on. The URL was an output of this
  // state and not an input to it. (Same fix as Agent OS, same bug.)
  useEffect(() => {
    const follow = () => {
      const asked = parseHash(window.location.hash).id;
      const wanted = asked === "library" ? "apps" : asked;
      if (SECTIONS.some((s) => s.id === wanted)) setCurrent(wanted);
    };
    window.addEventListener("hashchange", follow);
    return () => window.removeEventListener("hashchange", follow);
  }, []);

  const get = useMemo(
    () => (session ? kernel(session.token) : null),
    [session],
  );
  const post = useMemo(
    () => (session ? kernelPost(session.token) : null),
    [session],
  );
  // Its own helper rather than an option on `post`, because removing somebody
  // and writing them are separate grants — `user.set` and `user.remove` are
  // two actions for the same reason `access.grant` and `access.revoke` are.
  const remove = useMemo(
    () => (session ? kernelDelete(session.token) : null),
    [session],
  );

  const section = SECTIONS.find((s) => s.id === current) || SECTIONS[0];
  const Section = section.component;
  const onEffective = useCallback((next) => setEffective(next), []);

  if (!ready) {
    return <Loading label="Restoring your session" minHeight="100vh" />;
  }

  if (!session) {
    return (
      <SignedOut
        error={error}
        signedOut={new URLSearchParams(window.location.search).has("signedout")}
      />
    );
  }

  const groups = session.claims["cognito:groups"] || [];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* A hairline of brand across the top of the app, and nothing else
          coloured in the chrome. It is the only place the accent appears
          without carrying a meaning — permitted, refused, attention and
          unattended own the semantic space, and a console that tinted its
          furniture in the same green as a permitted tag would make one of
          them stop reading as a signal.

          Two pixels is enough to say a person is inside something rather
          than looking at a page of white boxes. */}
      <Box sx={{ height: 2, bgcolor: "primary.main" }} />
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Brand />
          <Box sx={{ flex: 1 }} />
          <Chip
            size="small"
            variant="outlined"
            label={session.claims.username || session.claims.sub}
          />
          <SchemeToggle />
          <Button size="small" onClick={signOut}>
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: NAV_WIDTH,
              position: "static",
              // The section questions wrap, and without this the wrapped text
              // pushed a horizontal scrollbar into the nav — a scrollbar on a
              // six-item list reads as something being cut off.
              overflowX: "hidden",
              borderRight: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            },
          }}
        >
          <Nav current={current} onSelect={setCurrent} />
        </Drawer>

        <Box sx={{ flex: 1, minWidth: 0, p: { xs: 2, md: 3 } }}>
          <Stack spacing={2}>
            {/* The screen's name, and the question it answers set as the
                question rather than as a subtitle. The question is the thing
                worth reading — it is what tells somebody whether they are on
                the right screen — and it was two thirds the size of a label
                they already clicked. */}
            <Box sx={{ pt: { xs: 0.5, md: 1 } }}>
              <Typography
                variant="overline"
                color="primary.main"
                sx={{ display: "block" }}
              >
                {section.label}
              </Typography>
              <Typography variant="h1" sx={{ maxWidth: "34ch" }}>
                {section.question}
              </Typography>
            </Box>

            {/* The product's argument, in the shell rather than remembered
                per page. */}
            <Permission groups={groups} effective={effective} gate={section.gate} />

            <Divider />

            {/* `session` as well as `get`/`post`, for the one screen that
                talks to a second API. The Library reads the control plane,
                which needs the raw token to build its own client — the same
                token, a different audience. Passing the session rather than a
                third pre-built client keeps the shell from knowing which
                sections use which API. */}
            <Section
              get={get}
              post={post}
              remove={remove}
              token={session.token}
              session={session}
              claims={session.claims}
              onEffective={onEffective}
            />
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
